import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
    ViewChild
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { RequestProductGrowthComponent } from "@app/shared/components/request-product-growth/request-product-growth.component";
import { ContactService } from "@app/shared/services/contact.service";
import { PeriodNames } from "@arch/_shared/types/interval-dates";
import {
    DREVerticalReferences,
    VisualizationOptionsType
} from "@arch/_shared/types/options-visualization";
import { DateUtils } from "@arch/_shared/utils/date.utils";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ReportEntity, ScenarioEntity } from "@arch/domain/entity";
import { CompanyEntity } from "@arch/domain/entity/company.entity";
import { GetAvailableController } from "@arch/presentation/controllers";
import { PopoverComponent, PopoverTemplate } from "@ngx-popovers/popover";
import { AppAvailableDatesSelectorComponent } from "@shared/components/app-dates-interval-selector/app-available-dates-selector.component";
import { BadgeRocketComponent } from "@shared/components/badge-rocket/badge-rocket.component";
import { FormDreBalanceOptionsComponent } from "@shared/components/form-dre-balance-options/form-dre-balance-options.component";
import { SharedDsnModule } from "@shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@shared/modules/shared-material.module";
import { DatePeriodService } from "@shared/services/date-period.service";
import { DeviceService } from "@shared/services/device.service";
import { DrawerService } from "@shared/services/drawer.service";

@Component({
    selector: "app-report-toolbar",
    imports: [
        FormsModule,
        SharedDsnModule,
        SharedMaterialModule,
        PopoverComponent,
        PopoverTemplate,
        AppAvailableDatesSelectorComponent,
        FormDreBalanceOptionsComponent,
        BadgeRocketComponent
    ],
    templateUrl: "./report-toolbar.component.html",
    styleUrl: "./report-toolbar.component.scss"
})
export class ReportToolbarComponent {
    @Input({ required: true }) set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    @Input({ required: false }) adjustments: boolean = true;
    get options() {
        return this.optionsSignal();
    }
    private optionsSignal = signal<VisualizationOptionsType>(null);
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: false }) loading: boolean;
    @Input({ required: false }) scenarios: ScenarioEntity[];
    @Input({ required: false }) budgets: ScenarioEntity[];
    @Input({ required: false }) projections: ScenarioEntity[];
    @Input({ required: false }) dropdowns: {
        period?: boolean;
        comparation?: boolean;
        budget?: boolean;
        projection?: boolean;
        goals?: boolean;
    };
    @Input({ required: false }) dropdownConfigs: {
        period?: {
            equalYear: boolean;
        };
        comparation?: {
            equalYear: boolean;
            fixedActions?: boolean;
        };
    };
    @Input({ required: false }) report: ReportEntity;
    @Input({ required: false }) pdf: boolean = false;
    @Input({ required: false }) linkDigital: string = "";
    @Output() handleResetPeriod = new EventEmitter<void>();
    @Output() handleChangePeriod = new EventEmitter<EventChangePeriodParams>();
    @Output() handleChangeComparation =
        new EventEmitter<EventChangeComparationParams>();
    @Output() handleChangeBudget = new EventEmitter<ScenarioEntity>();
    @Output() handleResetBudget = new EventEmitter<void>();
    @Output() handleChangeProjection = new EventEmitter<ScenarioEntity>();
    @Output() handleResetProjection = new EventEmitter<void>();
    @Output() handleChangeGoals = new EventEmitter<void>();
    @Output() handleResetGoals = new EventEmitter<void>();

    @ViewChild(PopoverComponent) popoverPeriod: PopoverComponent;
    @ViewChild(PopoverComponent) popoverComparation: PopoverComponent;

    private readonly deviceService = inject(DeviceService);
    private readonly drawerService = inject(DrawerService);
    private readonly datePeriodService = inject(DatePeriodService);
    private readonly eventsService = inject(EventsService);
    private readonly getAvailableController = inject(GetAvailableController);
    private readonly contactService = inject(ContactService);
    private readonly dialog = inject(MatDialog);

    public contact = this.contactService.get();
    public isMobile = this.deviceService.getIsMobile();
    public formBudget: { _id: string } = { _id: "" };
    public formProjection: { _id: string } = { _id: "" };
    public formDreBalanceOptions: {
        analysisHorizontal: boolean;
        analysisVertical: boolean;
        verticalReference: DREVerticalReferences;
    } = {
        analysisHorizontal: false,
        analysisVertical: false,
        verticalReference: "RECEITA BRUTA"
    };

    constructor() {
        effect(() => {
            if (!this.options) return null;
            if (this.dropdownConfigs?.comparation)
                this.dropdownConfigs.comparation.fixedActions = true;
            this.formBudget._id = this.options?.budget || "";
            this.formProjection._id =
                this.options?.projection || "PROGRESSAO_LINEAR";
            this.formDreBalanceOptions.analysisHorizontal =
                this.options.dre?.analysisHorizontal ||
                this.options.balance?.analysisHorizontal;
            this.formDreBalanceOptions.analysisVertical =
                (this.options.dre?.analysisVertical ||
                    this.options.balance?.analysisVertical) &&
                this.options.comparation.active;
            this.formDreBalanceOptions.verticalReference =
                this.options.dre?.verticalReference;
        });
    }

    public readonly periodLabel = computed((): string => {
        if (!this.options?.comparation?.active) return "Período";
        const label = DateUtils.formatByPeriodName(
            this.options.comparation.allMonths ? "yearly" : this.options.period,
            this.options.endDate,
            true
        );
        return label;
    });

    public readonly comparationLabel = computed((): string => {
        if (!this.options) return "";
        if (!this.options?.comparation?.active) return "Comparação";
        const label = DateUtils.formatByPeriodName(
            this.options.comparation.allMonths
                ? "yearly"
                : this.options.comparation.period,
            this.options.comparation.date,
            true
        );
        return label;
    });

    public readonly dateLabel = computed((): string => {
        if (!this.options) return "";
        return this.datePeriodService.getDateLabel(this.options, this.pdf);
    });

    public readonly periodAvailable = computed((): Date[] => {
        if (!this.options) return null;
        const available = this.getAvailableController.execute({
            companyId: this.company.props._id,
            source: this.options.source,
            sources: this.options.sources,
            scenarioId: this.options.scenario.props._id,
            options: {
                budget: this.options.budget,
                projection: this.options.projection
            }
        });

        return available;
    });

    public readonly comparationAvailable = computed((): Date[] => {
        if (!this.options) return null;
        const available = this.getAvailableController.execute({
            companyId: this.company.props._id,
            source: this.options.source,
            sources: this.options.sources,
            scenarioId: this.options.comparation.scenario.props._id
        });
        return available;
    });

    public onSelectScenarioPeriod(event: ScenarioEntity): void {
        this.optionsSignal.update((prev) => ({
            ...prev,
            scenario: event
        }));
    }

    public onSelectScenarioComparation(event: ScenarioEntity): void {
        this.optionsSignal.update((prev) => ({
            ...prev,
            comparation: {
                ...prev.comparation,
                scenario: event
            }
        }));
    }

    public onAdjustmentsClick(): void {
        this.drawerService.toggleDrawer();
    }

    public onApplyPeriod(period: {
        scenario: ScenarioEntity;
        periodName: PeriodNames;
        startDate: Date;
        endDate: Date;
    }): void {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.periodo`
        );
        this.popoverPeriod.close();
        this.handleChangePeriod.emit(period);
    }

    public onApplyComparation(period: {
        scenario: ScenarioEntity;
        periodName: PeriodNames;
        startDate: Date;
        endDate: Date;
        allMonthsCompare?: boolean;
    }): void {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.comparacao`
        );
        this.popoverComparation.close();
        const eventParams: EventChangeComparationParams = {
            scenario: period.scenario,
            periodName: period.periodName,
            dateBase: period.startDate,
            comparationDate: period.endDate,
            allMonthsCompare: period.allMonthsCompare
        };
        if (this.report.hasBalance) {
            eventParams.balance = {
                analysisHorizontal:
                    this.formDreBalanceOptions.analysisHorizontal,
                analysisVertical: this.formDreBalanceOptions.analysisVertical
            };
        }
        if (["dre", "dreComparation"].includes(this.report?.type)) {
            eventParams.dre = {
                analysisHorizontal:
                    this.formDreBalanceOptions.analysisHorizontal,
                analysisVertical: this.formDreBalanceOptions.analysisVertical,
                verticalReference: this.formDreBalanceOptions.verticalReference
            };
        }
        this.handleChangeComparation.emit(eventParams);
    }

    public onResetComparation(): void {
        if (this.popoverComparation) {
            this.popoverComparation.close();
        }

        this.handleResetPeriod.emit();
    }

    public onSelectBudget(event: any): void {
        this.formBudget._id = event;
    }

    public onSelectProjection(event: any): void {
        this.formProjection._id = event;
    }

    public onApplyBudget(): void {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.orcamento`
        );
        const budget = this.budgets.find(
            (budget) => budget.props._id === this.formBudget._id
        );
        this.handleChangeBudget.emit(budget);
    }

    public onApplyProjection(): void {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.projeção`
        );
        const projection = this.projections.find(
            (projection) => projection.props._id === this.formProjection._id
        );
        this.handleChangeProjection.emit(projection);
    }

    public onResetProjection(): void {
        this.handleResetProjection.emit();
    }

    public onRequestAnalysisPlus(): void {
        this.dialog.open(RequestProductGrowthComponent, {
            width: "386px",
            maxWidth: "386px",
            data: {
                title: "Análise Plus",
                imageSrc:
                    "https://nucont-imgs.s3.amazonaws.com/novidades/analise-plus-objetivos.gif",
                subtitle: "Projeção, orçamento e objetivos",
                description:
                    "Com a Análise Plus, você antecipa resultados com <span class='font-weight-semi-bold'>Projeções</span>, controla despesas e receitas, acompanha o <span class='font-weight-semi-bold'>Orçamento</span> e define <span class='font-weight-semi-bold'>Objetivos</span> por indicador.",
                hint: "Essa funcionalidade <span class='font-weight-semi-bold'>não está disponível</span> no seu plano atual. Solicite o contato abaixo para incluir a Análise Plus no seu plano.",
                cta: "Solicitar Análise Plus",
                company: this.company,
                package: "Análise Plus",
                licenses: 1,
                options: {
                    addedValue: "0",
                    period: ""
                }
            }
        });
    }
}

export type EventChangeComparationParams = {
    scenario: ScenarioEntity;
    periodName: PeriodNames;
    dateBase: Date;
    comparationDate: Date;
    allMonthsCompare?: boolean;
    dre?: {
        analysisHorizontal: boolean;
        analysisVertical: boolean;
        verticalReference: DREVerticalReferences;
    };
    balance?: {
        analysisHorizontal: boolean;
        analysisVertical: boolean;
    };
};

export type EventChangePeriodParams = {
    scenario: ScenarioEntity;
    periodName: PeriodNames;
    startDate: Date;
    endDate: Date;
};
